import type { UploadWithProgressPayload } from '@components/upload-progress-bar/types.ts';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { CreatorRevision } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CommonCreatorPathwaysRequest,
    CreatorPathway,
    CreatorPathwayResponse,
    PathwaySectionResponse,
    PostPathwayRequest,
    PutPathwayRequest,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/api/creatorPathways.api.ts';
import {
    createApiCreatorPathway,
    deleteApiCreatorPathway,
    getApiCreatorPathwayById,
    getApiCreatorPathways,
    updateApiCreatorPathway,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/api/creatorPathways.api.ts';
import { getPathwayElementIds } from '@routes/sidebar/admin-center/creator/___pages___/pathways/model/utils.ts';
import type { UploadContentResponse } from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import { createApiPublicResourceWithUploadProgress } from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';
import { isEmpty } from 'lodash';

interface CreatorPathwaysError {
    message?: string;
    [k: string]: any;
}

interface PathwayDefinition {
    type: string;
    category: string;
    createdFor: string;
}

interface CreatorPathwaysState {
    error: CreatorPathwaysError | null;
    creatorPathways: PaginatedResponse<CreatorPathway, EmptyResultsDescription> | null;
    selectedPathway: CreatorPathway | null;
    pathwayElementsSections: PathwaySectionResponse[];
    pathwayRevisions: CreatorRevision[];
    uploadedCoverImageUri: string | null;
    uploadedCoverImageId: string | null;
    isArchivePathwayModalOpened: boolean;
    isRemoveElementModalOpened: boolean;
    isArchivePathwayFailedModalOpened: boolean;
    isPublishPathwayModalOpened: boolean;
    isCoverImageModalOpened: boolean;
    isCoverImageUploaded: boolean;
    currentGetRequestId: string | null;
    recordsRowOrder: string[];
    isModalOpened: boolean;
    rawSelectedPathwayElementsIds: string[];
    pathwayDefinition: PathwayDefinition | null;

    isGetCreatorPathwaysPending: boolean;
    isGetCreatorPathwayPending: boolean;
    isCreateCreatorPathwayPending: boolean;
    isUpdateCreatorPathwayPending: boolean;
    isDeleteCreatorPathwayPending: boolean;
    isUploadPublicContentPending: boolean;
}

const initialState: CreatorPathwaysState = {
    error: null,
    creatorPathways: null,
    selectedPathway: null,
    pathwayElementsSections: [],
    pathwayRevisions: [],
    isArchivePathwayModalOpened: false,
    isRemoveElementModalOpened: false,
    isArchivePathwayFailedModalOpened: false,
    isPublishPathwayModalOpened: false,
    currentGetRequestId: null,
    recordsRowOrder: [],
    isModalOpened: false,
    rawSelectedPathwayElementsIds: [],
    pathwayDefinition: null,

    isGetCreatorPathwaysPending: false,
    isGetCreatorPathwayPending: false,
    isCreateCreatorPathwayPending: false,
    isUpdateCreatorPathwayPending: false,
    isDeleteCreatorPathwayPending: false,
    isUploadPublicContentPending: false,
    isCoverImageModalOpened: false,
    isCoverImageUploaded: false,
    uploadedCoverImageUri: null,
    uploadedCoverImageId: null,
};

export const getCreatorPathways = createAsyncThunk<
    PaginatedResponse<CreatorPathway, EmptyResultsDescription>,
    { payload: CommonCreatorPathwaysRequest },
    { rejectValue: CreatorPathwaysError }
>('creatorPathways/getCreatorPathways', ({ payload }, { rejectWithValue }) =>
    getApiCreatorPathways(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCreatorPathwayById = createAsyncThunk<
    CreatorPathwayResponse,
    { pathwayId: string },
    { rejectValue: CreatorPathwaysError }
>('creatorPathways/getCreatorPathwayById', ({ pathwayId }, { rejectWithValue }) =>
    getApiCreatorPathwayById(pathwayId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createCreatorPathway = createAsyncThunk<
    CreatorPathwayResponse,
    { payload: PostPathwayRequest },
    { rejectValue: CreatorPathwaysError }
>('creatorPathways/createCreatorPathway', ({ payload }, { rejectWithValue }) =>
    createApiCreatorPathway(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCreatorPathway = createAsyncThunk<
    CreatorPathwayResponse,
    { id: string; payload: PutPathwayRequest },
    { rejectValue: CreatorPathwaysError }
>('creatorPathways/updateCreatorPathway', ({ id, payload }, { rejectWithValue }) =>
    updateApiCreatorPathway(id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteCreatorPathway = createAsyncThunk<
    any,
    { id: string; comment: string | null },
    { rejectValue: CreatorPathwaysError }
>('creatorPathways/deleteCreatorPathway', ({ id, comment }, { rejectWithValue }) =>
    deleteApiCreatorPathway({ id, comment })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createCoverImageWithUploadProgress = createAsyncThunk<
    UploadContentResponse[],
    UploadWithProgressPayload,
    { rejectValue: string[] }
>('creatorPathways/uploadContentPublic', ({ formData, onUploadProgress, signal }, { rejectWithValue }) =>
    createApiPublicResourceWithUploadProgress(formData, onUploadProgress, signal)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const creatorPathwaysSlice = createSlice({
    name: 'creatorPathways',
    initialState,
    reducers: {
        setIsCoverImageModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isCoverImageModalOpened = action.payload;
        },
        setIsCoverImageUploaded: (state, action: PayloadAction<boolean>) => {
            state.isCoverImageUploaded = action.payload;
        },
        clearSelectedPathway: (state) => {
            state.selectedPathway = null;
            state.uploadedCoverImageUri = null;
            state.uploadedCoverImageId = null;
            state.isCoverImageModalOpened = false;
            state.isCoverImageUploaded = false;
            state.isUploadPublicContentPending = false;
            state.pathwayElementsSections = [];
        },
        setSelectedPathway(state, action: PayloadAction<CreatorPathway | null>) {
            state.selectedPathway = action.payload;
        },
        setIsArchivePathwayModalOpened(state, action: PayloadAction<boolean>) {
            state.isArchivePathwayModalOpened = action.payload;
        },
        setIsArchivePathwayFailedModalOpened(state, action: PayloadAction<boolean>) {
            state.isArchivePathwayFailedModalOpened = action.payload;
        },
        setIsRemoveElementModalOpened(state, action: PayloadAction<boolean>) {
            state.isRemoveElementModalOpened = action.payload;
        },
        setIsPublishPathwayModalOpened(state, action: PayloadAction<boolean>) {
            state.isPublishPathwayModalOpened = action.payload;
        },

        // elements tab
        setRecordsRowOrder: (state, action: PayloadAction<string[]>) => {
            state.recordsRowOrder = action.payload;
        },
        setIsModalOpened(state, action: PayloadAction<boolean>) {
            state.isModalOpened = action.payload;
        },
        setSelectedPathwayElements(state, action: PayloadAction<string[]>) {
            state.rawSelectedPathwayElementsIds = action.payload;
        },
        setPathwayDefinition(state, action: PayloadAction<PathwayDefinition | null>) {
            state.pathwayDefinition = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCreatorPathways.pending, (state, action) => {
            state.isGetCreatorPathwaysPending = true;
            state.currentGetRequestId = action.meta.requestId;
            state.error = null;
        });
        builder.addCase(getCreatorPathways.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCreatorPathwaysPending = false;
            state.currentGetRequestId = null;
            state.creatorPathways = action.payload;
        });
        builder.addCase(getCreatorPathways.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCreatorPathwaysPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(getCreatorPathwayById.pending, (state) => {
            state.isGetCreatorPathwayPending = true;
            state.error = null;
        });
        builder.addCase(getCreatorPathwayById.fulfilled, (state, action) => {
            state.isGetCreatorPathwayPending = false;
            state.selectedPathway = action.payload;
            state.pathwayElementsSections = action.payload.sections;
            state.pathwayRevisions = action.payload.revisions;
            state.rawSelectedPathwayElementsIds = getPathwayElementIds(action.payload.sections);
        });
        builder.addCase(getCreatorPathwayById.rejected, (state, action) => {
            state.isGetCreatorPathwayPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createCreatorPathway.pending, (state) => {
            state.isCreateCreatorPathwayPending = true;
            state.error = null;
        });
        builder.addCase(createCreatorPathway.fulfilled, (state, action) => {
            state.isCreateCreatorPathwayPending = false;
            state.selectedPathway = action.payload;
            state.pathwayElementsSections = action.payload.sections;
            state.pathwayRevisions = action.payload.revisions;
            state.rawSelectedPathwayElementsIds = getPathwayElementIds(action.payload.sections);
        });
        builder.addCase(createCreatorPathway.rejected, (state, action) => {
            state.isCreateCreatorPathwayPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateCreatorPathway.pending, (state) => {
            state.isUpdateCreatorPathwayPending = true;
            state.error = null;
        });
        builder.addCase(updateCreatorPathway.fulfilled, (state, action) => {
            state.isUpdateCreatorPathwayPending = false;
            state.selectedPathway = action.payload;
            state.pathwayElementsSections = action.payload.sections;
            state.pathwayRevisions = action.payload.revisions;
            state.rawSelectedPathwayElementsIds = getPathwayElementIds(action.payload.sections);
        });
        builder.addCase(updateCreatorPathway.rejected, (state, action) => {
            state.isUpdateCreatorPathwayPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteCreatorPathway.pending, (state) => {
            state.isDeleteCreatorPathwayPending = true;
            state.error = null;
        });
        builder.addCase(deleteCreatorPathway.fulfilled, (state) => {
            state.isDeleteCreatorPathwayPending = false;
        });
        builder.addCase(deleteCreatorPathway.rejected, (state, action) => {
            state.isDeleteCreatorPathwayPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createCoverImageWithUploadProgress.pending, (state) => {
            state.isUploadPublicContentPending = true;
            state.error = null;
        });

        builder.addCase(createCoverImageWithUploadProgress.fulfilled, (state, action) => {
            if (isEmpty(action.payload)) {
                state.uploadedCoverImageUri = null;
                state.uploadedCoverImageId = null;
            } else {
                state.uploadedCoverImageUri = action.payload[0].outputUrl;
                state.uploadedCoverImageId = action.payload[0].contentId;
            }
            state.isUploadPublicContentPending = false;
        });

        builder.addCase(createCoverImageWithUploadProgress.rejected, (state, action) => {
            state.isUploadPublicContentPending = false;
            state.uploadedCoverImageUri = null;
            state.uploadedCoverImageId = null;
            state.error = extractError(action);
        });
    },
});

export const {
    setIsRemoveElementModalOpened,
    setSelectedPathway,
    setIsArchivePathwayModalOpened,
    setIsPublishPathwayModalOpened,
    setIsArchivePathwayFailedModalOpened,
    setIsCoverImageModalOpened,
    setIsCoverImageUploaded,
    clearSelectedPathway,
    setPathwayDefinition,
    setRecordsRowOrder,
    setSelectedPathwayElements,
    setIsModalOpened,
} = creatorPathwaysSlice.actions;

export default creatorPathwaysSlice.reducer;
